import { useMemo } from 'react';
import { DataTable, DataTableBody, DataTableFooter } from '@optiaxiom/react';
import {
  type ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  type RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';

import type { NotificationRow, Pagination } from '../../types/notification';

import { createColumns } from './columns';

type NotificationTableProps = {
  rows: NotificationRow[];
  pagination: Pagination | undefined;
  isLoading: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  rowSelection: RowSelectionState;
  onRowSelectionChange: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  expanded: ExpandedState;
  onExpandedChange: React.Dispatch<React.SetStateAction<ExpandedState>>;
  onActivate: (row: NotificationRow) => void;
  onMarkRead: (id: string) => void;
  onMarkUnread: (id: string) => void;
  onDelete: (id: string, type: string) => void;
  onGroupMarkRead: (groupId: string) => void;
  onGroupDelete: (groupId: string, type: string) => void;
};

export function NotificationTable({
  rows,
  pagination,
  isLoading,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  rowSelection,
  onRowSelectionChange,
  expanded,
  onExpandedChange,
  onActivate,
  onMarkRead,
  onMarkUnread,
  onDelete,
  onGroupMarkRead,
  onGroupDelete,
}: NotificationTableProps) {
  const columns = useMemo(
    () =>
      createColumns({
        onActivate,
        onMarkRead,
        onMarkUnread,
        onDelete,
        onGroupMarkRead,
        onGroupDelete,
      }),
    [onActivate, onMarkRead, onMarkUnread, onDelete, onGroupMarkRead, onGroupDelete]
  );

  const totalCount = pagination?.type === 'offset' ? pagination.total_count : 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const paginationState = useMemo(
    () => ({
      pageIndex: page - 1,
      pageSize,
    }),
    [page, pageSize]
  );

  const table = useReactTable({
    columns,
    data: rows,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: row => row.subRows,
    getRowId: row => row.id,
    manualPagination: true,
    rowCount: totalCount,
    pageCount: totalPages,
    state: {
      pagination: paginationState,
      rowSelection,
      expanded,
      columnPinning: { right: ['actions'] },
    },
    onRowSelectionChange,
    onExpandedChange,
    onPaginationChange: updater => {
      const newPag = typeof updater === 'function' ? updater(paginationState) : updater;
      if (newPag.pageIndex !== paginationState.pageIndex) {
        onPageChange(newPag.pageIndex + 1);
      }
      if (newPag.pageSize !== paginationState.pageSize) {
        onPageSizeChange(newPag.pageSize);
      }
    },
  });

  return (
    <DataTable table={table}>
      <DataTableBody loading={isLoading} />
      <DataTableFooter />
    </DataTable>
  );
}
